import {IModule, SystemComponentType, ComponentModuleViewConfig, ISoundPlayer} from '@gongxigames/framework/core/types';
import {injectable, inject} from '@gongxigames/framework/core/installer';
import {consoleLogger} from '@gongxigames/framework/core/fragments/ConsoleLogger';
import {GameOnlyEventMap, slotStore, strictGameEmitter} from '@gongxigames/framework/slotV2/globals';
import {Container} from 'pixi.js';
import {toString} from '@gongxigames/framework/core/utils/componentNameToString';
import {CoreDI} from '@gongxigames/framework/core/configs';
import {UpdateManager} from '@gongxigames/framework/core/services/UpdateManager';
import {DI} from '@gongxigames/framework/slot/configs/DI';
import {SoundManager} from '@gongxigames/framework/slot/services/SoundManager';
import {LAYERS} from '@gongxigames/framework/slot/configs/Layers';
import {ModuleSignalEventEmitterWrapper} from '@gongxigames/framework/core/services/strictEventEmitter/ModuleSignalEventEmitterWrapper';
import {StrictEventEmitter} from '@gongxigames/framework/core/services/strictEventEmitter';

type ModuleEvents = `Module.${string}`;

@injectable()
export class SlotModuleV2<T extends GameOnlyEventMap> implements IModule {
    public static layer = LAYERS.UNDER_REELS;

    public readonly type: SystemComponentType = 'module';

    @inject(CoreDI.UpdateManager)
    protected _updateManager!: UpdateManager;

    // TODO: maybe it can be removed
    // Why we have DI.SoundManager & DI.SoundPlayer?
    // DI.SoundPlayer should be enough
    // looks like this API that needs refactoring
    @inject(DI.SoundManager)
    protected _soundManager!: SoundManager;

    @inject(DI.SoundPlayer)
    protected _soundPlayer!: ISoundPlayer;

    private readonly abortController = new AbortController();
    protected readonly abortSignal = this.abortController.signal;
    readonly emitter: ModuleSignalEventEmitterWrapper<T> =
        new ModuleSignalEventEmitterWrapper(strictGameEmitter as unknown as StrictEventEmitter<T>, {signal: this.abortSignal, owner: this});

    protected readonly store = slotStore.asReadOnlyStore();

    public container = new Container();

    public configure<C extends ComponentModuleViewConfig>(config: C): void {
        this.setupContainer(config);
        this.onConfigure(config);
    }

    public init(): void {
        this.onInit();
        this.onSetupEvents();
    }

    public restore(): void {
        this.onRestore();
    }

    public destroy(): void {
        this.abortController.abort();
        this.onDestroy();
        this.container?.destroy({children: true});
        consoleLogger.log(`Module ${this.constructor.name} destroyed`);
    }

    protected onConfigure<C extends ComponentModuleViewConfig>(config: C): void {}
    protected onInit(): void {}
    protected onSetupEvents(): void {}
    protected onRestore(): void {}
    protected onDestroy(): void {}

    protected getParentContainer(): Container {
        return this.store.stage_rootContainer_game.value;
    }

    protected setupContainer<C extends ComponentModuleViewConfig>(config: C): void {
        const moduleName = toString(config.NAME);
        const parent = this.getParentContainer();

        this.container.name = moduleName;
        this.container.setParent(parent);

        this.container.parentGroup = config.layer.group;
    }
}